import { MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesPrimary,
  Item,
  Loader,
  LoaderType,
  MessageBar,
  useClassNames,
  useTheme,
} from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { NatGwRequest } from '../../admin-center/gen/environment_info/natgw_pb';
import { useAdminCenterService } from '../../admin-center/hooks';
import { useQueryEnvironment } from '../../discovery-service/discoveryService.hooks';
import { useUser } from '../../utils/hooks';
import { ClassNamesFromIStyles } from '../../utils/models';
import { buildUserContext } from './common/utils';
import { envInfoStyles, IEnvInfoPageStyles, loaderStyles, versionTagStyles } from './components/EnvInfo/EnvInfo.styles';
import PageWrapper from './PageWrapper';

export default function EnvInfo() {
  const [natGwIps, setNatGwIps] = useState<string[]>([]);
  const [loadIpsSuccess, setLoadIpsSuccess] = useState<boolean>(true);

  const environmentQuery = useQueryEnvironment();
  const cloudVersionPropertyValue = environmentQuery.data?.h2oCloudVersion || `Unknown`;
  const service = useAdminCenterService();

  const theme = useTheme();
  const user = useUser();
  const classNames = useClassNames<IEnvInfoPageStyles, ClassNamesFromIStyles<IEnvInfoPageStyles>>(
    'envInfo',
    envInfoStyles(theme)
  );

  const fetchIps = async () => {
    try {
      const userContext = await buildUserContext(user);
      const req: NatGwRequest = {
        userContext: userContext,
      };
      const response = await service.getNatGwPublicIps(req);
      const ips = response.publicIps || [];

      setNatGwIps(ips);
    } catch (error) {
      setLoadIpsSuccess(false);
      console.error('Error fetching NAT gateway IPs:', error);
    }
  };

  useEffect(() => {
    fetchIps();
  }, []);

  const handleReleaseNotesButtonClick = () => {
    window.open('https://docs.h2o.ai/haic-documentation/mc-release-notes/', '_blank');
  };

  const handleDocumentButtonClick = () => {
    window.open('https://docs.h2o.ai/mc-admin-center/', '_blank');
  };

  return (
    <PageWrapper>
      <div className={classNames.container}>
        <div className={classNames.content}>
          <div className={classNames.header}>
            <div className={classNames.versionInfo}>
              <div>Managed Cloud Version:</div>
              <Item
                data={{
                  id: 0,
                  name: cloudVersionPropertyValue,
                }}
                styles={versionTagStyles}
                idField="id"
                labelField="name"
              />
            </div>
            <div className={classNames.headerButton}>
              <Button
                styles={buttonStylesPrimary}
                iconName="FileSymlink"
                text="Managed Cloud Release Notes"
                onClick={handleReleaseNotesButtonClick}
              />
              <Button
                styles={buttonStylesPrimary}
                iconName="FileSymlink"
                text="Cloud Environment Document"
                onClick={handleDocumentButtonClick}
              />
            </div>
          </div>

          <div className={classNames.card}>
            <div className={classNames.cardHeader}>Environment Public IPs</div>
            <div className={classNames.cardInfo}>
              All outbound connections from your managed cloud environment will be initiated from these IPs
            </div>
            <div className={classNames.ipList}>
              {!loadIpsSuccess ? (
                <MessageBar messageBarType={MessageBarType.error}>{"Couldn't load NAT Gateway IPs"}</MessageBar>
              ) : natGwIps.length > 0 ? (
                natGwIps.map((ip, index) => (
                  <div key={index} className={classNames.ipItem}>
                    <div className={classNames.ipStatus}>
                      <div className={classNames.statusDot} />
                      <p>{ip}</p>
                    </div>
                  </div>
                ))
              ) : (
                <Loader styles={loaderStyles(theme)} type={LoaderType.progressIndicator} />
              )}
            </div>
          </div>
          <MessageBar messageBarType={MessageBarType.info}>
            {
              'All data on every page is shown in your local timezone. AI Unit Reporting and Analytics data are updated twice per day.'
            }
          </MessageBar>
        </div>
      </div>
    </PageWrapper>
  );
}
